import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, Coins, ShieldCheck, Star, Gift } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useCredits, PLAN_CREDITS } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const plans = [
  {
    name: "Free",
    price: "₹0",
    priceValue: 0,
    period: "forever",
    planId: "free",
    description: "Perfect for trying out NoteGenius",
    credits: PLAN_CREDITS.free,
    features: [
      `${PLAN_CREDITS.free} credits to start`,
      "Basic text extraction",
      "Standard quality outputs",
      "Email support",
    ],
    cta: "Get Started",
    variant: "outline" as const,
    popular: false,
    testimonial: null as null | { quote: string; author: string },
  },
  {
    name: "Pro",
    price: "₹29",
    priceValue: 29,
    period: "/month",
    planId: "pro",
    description: "Best for regular students",
    credits: PLAN_CREDITS.pro,
    features: [
      `${PLAN_CREDITS.pro} credits per purchase`,
      "Advanced AI processing",
      "PPT & document generation",
      "Priority support",
      "No watermarks",
    ],
    cta: "Buy Pro Credits",
    variant: "hero" as const,
    popular: true,
    testimonial: {
      quote: "Cleared my semester prep in 2 days. Worth every rupee.",
      author: "Ananya S., DU 2nd year",
    },
  },
  {
    name: "Premium",
    price: "₹49",
    priceValue: 49,
    period: "/month",
    planId: "premium",
    description: "For power users & groups",
    credits: PLAN_CREDITS.premium,
    features: [
      `${PLAN_CREDITS.premium} credits per purchase`,
      "Everything in Pro",
      "Team collaboration",
      "API access",
      "Custom templates",
      "Dedicated support",
    ],
    cta: "Buy Premium Credits",
    variant: "outline" as const,
    popular: false,
    testimonial: {
      quote: "Our study group runs entirely on Premium. Game changer.",
      author: "Rohit M., IIT Delhi",
    },
  },
];

const Pricing = () => {
  const { user } = useAuth();
  const { currentPlan, loading: subscriptionLoading } = useSubscription();
  const { credits, refetch: refetchCredits } = useCredits();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePlanSelect = async (planId: string) => {
    if (planId === "free") {
      if (!user) {
        navigate("/auth");
      }
      return;
    }

    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to subscribe to a plan.",
      });
      navigate("/auth");
      return;
    }

    // Allow repurchasing same plan for more credits
    if (currentPlan === planId && planId !== 'free') {
      // User can buy more credits for their current plan
    }

    setProcessingPlan(planId);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error("Failed to load payment gateway");
      }

      // Create order
      const { data, error } = await supabase.functions.invoke("create-razorpay-order", {
        body: { plan: planId },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const { orderId, amount, currency, keyId } = data;

      // Open Razorpay checkout
      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: "NoteGenius",
        description: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan Subscription`,
        order_id: orderId,
        handler: async (response: any) => {
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
              "verify-razorpay-payment",
              {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  plan: planId,
                },
              }
            );

            if (verifyError) throw verifyError;
            if (verifyData.error) throw new Error(verifyData.error);

            toast({
              title: "Payment Successful!",
              description: verifyData.message,
            });

            // Refetch credits and reload
            refetchCredits();
            setTimeout(() => window.location.reload(), 1000);
          } catch (err: any) {
            console.error("Payment verification error:", err);
            toast({
              title: "Payment Verification Failed",
              description: err.message || "Please contact support if payment was deducted.",
              variant: "destructive",
            });
          }
        },
        prefill: {
          email: user.email,
        },
        theme: {
          color: "#8B5CF6",
        },
        modal: {
          ondismiss: () => {
            setProcessingPlan(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      console.error("Payment error:", err);
      toast({
        title: "Payment Error",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPlan(null);
    }
  };

  const getButtonText = (plan: typeof plans[0]) => {
    if (subscriptionLoading) return "Loading...";
    if (processingPlan === plan.planId) return "Processing...";
    if (plan.planId === 'free') {
      return user ? "Current Tier" : plan.cta;
    }
    return plan.cta;
  };

  const isButtonDisabled = (plan: typeof plans[0]) => {
    if (plan.planId === 'free') return !!user;
    return subscriptionLoading || processingPlan !== null;
  };

  return (
    <section id="pricing" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">Pricing</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
            Simple, Credit-Based Pricing
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Buy credits and use them anytime. No subscriptions, no commitments.
          </p>

          {/* Trust strip */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              4.9/5 from 2,000+ students
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-green-500" />
              7-day money-back guarantee
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Gift className="w-4 h-4 text-accent" />
              +10 bonus credits on first purchase
            </span>
          </div>
          
          {/* Current Credits Display */}
          {user && (
            <div className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary/10 border border-primary/20">
              <Coins className="w-5 h-5 text-primary" />
              <span className="text-lg font-semibold text-foreground">
                You have <span className="text-primary">{credits}</span> credits
              </span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 ${
                plan.popular
                  ? "gradient-card border-2 border-primary shadow-glow"
                  : "bg-card border border-border shadow-card"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full gradient-primary text-primary-foreground text-xs font-semibold">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}

              {currentPlan === plan.planId && (
                <div className="absolute -top-3 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-500 text-gray-900 text-xs font-semibold">
                    <Check className="w-3 h-3" />
                    Active
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                  {plan.planId !== 'free' && (
                    <span className="text-muted-foreground text-sm">for {plan.credits} credits</span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm mt-2">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.testimonial && (
                <div className="mb-4 p-3 rounded-lg bg-muted/40 border border-border/50">
                  <div className="flex gap-0.5 mb-1.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-xs italic text-foreground leading-snug">
                    "{plan.testimonial.quote}"
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1 font-medium">
                    — {plan.testimonial.author}
                  </p>
                </div>
              )}

              {plan.planId !== "free" && (
                <div className="mb-3 flex items-center justify-center gap-1.5 text-[11px] text-green-600 dark:text-green-400 font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  7-day refund · No risk
                </div>
              )}

              <Button 
                variant={plan.variant} 
                className="w-full"
                onClick={() => handlePlanSelect(plan.planId)}
                disabled={isButtonDisabled(plan)}
              >
                {processingPlan === plan.planId && (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                )}
                {getButtonText(plan)}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
